import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgClass } from '@angular/common';
import { progressRingVariants, VxProgressRingSize } from './progress-ring.variants';

/** Geometry from the design: r=41 in a 100-unit viewBox, stroke 12,
 * each segment fills ~64% of its slice (58° of 90° at four segments). */
const RADIUS = 41;
const ARC_FRACTION = 0.645;

interface Segment {
  d: string;
  completed: boolean;
}

const point = (angleDeg: number): string => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return `${(50 + RADIUS * Math.cos(rad)).toFixed(3)} ${(50 + RADIUS * Math.sin(rad)).toFixed(3)}`;
};

/**
 * Segmented progress ring: `<vx-progress-ring [completed]="1" [total]="4" />`.
 * One arc per step, completed arcs in accent, remaining on the dark track.
 *
 * Decorative (aria-hidden) — always pair it with visible text conveying the
 * progress (e.g. the onboarding card's subtitle).
 */
@Component({
  selector: 'vx-progress-ring',
  templateUrl: './progress-ring.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass],
  host: {
    '[class]': 'classes()',
    'aria-hidden': 'true',
  },
})
export class VxProgressRingComponent {
  readonly completed = input.required<number>();

  readonly total = input.required<number>();

  readonly size = input<VxProgressRingSize>('md');

  protected readonly classes = computed(() =>
    progressRingVariants({ size: this.size() }),
  );

  protected readonly segments = computed<Segment[]>(() => {
    const total = Math.max(1, this.total());
    const span = 360 / total;
    const arc = span * ARC_FRACTION;
    const inset = (span - arc) / 2;
    return Array.from({ length: total }, (_, i) => {
      const start = i * span + inset;
      const end = start + arc;
      const largeArc = arc > 180 ? 1 : 0;
      return {
        d: `M ${point(start)} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${point(end)}`,
        completed: i < this.completed(),
      };
    });
  });
}
